// pages/RdpPanel.jsx — Remote Desktop (RDP) to a Windows EC2 instance, inside EC2 Health → Remote Console.
// Backend: POST /api/rdp/ticket + WS /ws/rdp (backend/rdpTunnel.js → guacd → direct TCP or SSM port forwarding).
// The password is sent once to get a 60-second ticket and is not stored anywhere.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import Guacamole from 'guacamole-common-js';
import { api } from '../context/AuthContext';

const LS_USER = 'rdp.user.';   // per instance: last username / domain (never the password)
const STATE = ['Idle', 'Connecting…', 'Waiting for server…', 'Connected', 'Disconnecting…', 'Disconnected'];
const ERRORS = {
  0x0201: 'Server busy (too many sessions).',
  0x0202: 'Remote desktop server timed out.',
  0x0203: 'Remote desktop server error.',
  0x0204: 'Remote desktop server is unavailable.',
  0x0205: 'Session was taken over by another connection.',
  0x0207: 'Could not reach the remote desktop server.',
  0x0208: 'Remote desktop server timed out.',
  0x0209: 'Session conflicts with another connection.',
  0x0301: 'Login failed: wrong username or password, or the account is not allowed to use Remote Desktop.',
  0x0303: 'Access denied.',
  0x0308: 'Browser timed out.',
  0x031D: 'Too many connections from this browser.',
};
const lsGet = (k) => { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (_) { return null; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (_) {} };

export default function RdpPanel({ x, account }) {
  const saved = lsGet(LS_USER + x.id) || {};
  const [username, setUsername] = useState(saved.username || 'Administrator');
  const [domain, setDomain] = useState(saved.domain || '');
  const [password, setPassword] = useState('');
  const [port, setPort] = useState(saved.port ? String(saved.port) : '');
  const portOk = port === '' || (/^\d{1,5}$/.test(port) && +port >= 1 && +port <= 65535);
  const [state, setState] = useState(0);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [clip, setClip] = useState('');
  const [showClip, setShowClip] = useState(false);
  const [scale, setScale] = useState(1);
  const wrapRef = useRef(null);
  const screenRef = useRef(null);
  const clientRef = useRef(null);
  const kbRef = useRef(null);

  const fit = useCallback(() => {
    const c = clientRef.current;
    const box = wrapRef.current;
    if (!c || !box) return;
    const d = c.getDisplay();
    const w = d.getWidth();
    const h = d.getHeight();
    if (!w || !h) return;
    const full = document.fullscreenElement === box;
    const availW = box.clientWidth;
    const availH = full ? box.clientHeight : Math.max(320, window.innerHeight * 0.72);
    const s = Math.min(availW / w, availH / h, 1);
    d.scale(s);
    setScale(s);
  }, []);

  const teardown = useCallback(() => {
    const c = clientRef.current;
    clientRef.current = null;
    if (kbRef.current) { kbRef.current.onkeydown = null; kbRef.current.onkeyup = null; kbRef.current = null; }
    if (c) { try { c.disconnect(); } catch (_) {} }
    if (screenRef.current) screenRef.current.innerHTML = '';
  }, []);
  useEffect(() => teardown, [teardown]);
  useEffect(() => { teardown(); setState(0); setErr(null); }, [x.id, teardown]);
  useEffect(() => {
    const onResize = () => fit();
    window.addEventListener('resize', onResize);
    document.addEventListener('fullscreenchange', onResize);
    return () => { window.removeEventListener('resize', onResize); document.removeEventListener('fullscreenchange', onResize); };
  }, [fit]);

  const connect = async (e) => {
    if (e) e.preventDefault();
    if (!username || !password || !portOk) return;
    teardown();
    setErr(null); setBusy(true); setState(1);
    lsSet(LS_USER + x.id, { username, domain, port: port || null });
    const box = wrapRef.current;
    const bw = box && box.parentElement ? box.parentElement.clientWidth : 1280;
    const width = Math.max(1280, Math.min(1920, Math.round(bw)));
    const height = Math.round(width * 9 / 16);
    let ticket;
    try {
      const r = await api.post('/api/rdp/ticket', {
        account, instanceId: x.id, username, password, domain, width, height, dpi: 96, port: port || null,
        timezone: (Intl.DateTimeFormat().resolvedOptions().timeZone) || 'Asia/Jakarta',
      });
      ticket = r.data.ticket;
    } catch (e2) {
      setBusy(false); setState(0);
      setErr((e2.response && e2.response.data && e2.response.data.error) || e2.message);
      return;
    }
    setPassword('');
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const tunnel = new Guacamole.WebSocketTunnel(`${proto}//${window.location.host}/ws/rdp`);
    tunnel.receiveTimeout = 30000;
    const client = new Guacamole.Client(tunnel);
    clientRef.current = client;
    const display = client.getDisplay();
    const el = display.getElement();
    el.style.cursor = 'none';
    screenRef.current.innerHTML = '';
    screenRef.current.appendChild(el);
    display.onresize = () => fit();

    client.onstatechange = (s) => {
      setState(s);
      if (s === 3) { setBusy(false); setTimeout(() => { fit(); if (screenRef.current) screenRef.current.focus(); }, 50); }
      if (s === 5) { setBusy(false); }
    };
    client.onerror = (st) => {
      setBusy(false);
      setErr((st && st.message && !/^\s*$/.test(st.message) ? st.message : null) || ERRORS[st && st.code] || `Remote desktop error (0x${((st && st.code) || 0).toString(16)})`);
    };
    tunnel.onerror = (st) => { setBusy(false); setErr(ERRORS[st && st.code] || (st && st.message) || 'Tunnel error'); };
    client.onclipboard = (stream, mimetype) => {
      if (!/^text\//.test(mimetype)) return;
      const reader = new Guacamole.StringReader(stream);
      let data = '';
      reader.ontext = (t) => { data += t; };
      reader.onend = () => { setClip(data); if (navigator.clipboard) navigator.clipboard.writeText(data).catch(() => {}); };
    };

    // Mouse. Guacamole.Position walks offsetParent/scrollTop, which is wrong inside this scrolled,
    // fixed-position modal (the pointer got stuck half-way down). So the pointer position is taken
    // from the real client coordinates against getBoundingClientRect, and only the button state
    // comes from Guacamole.Mouse.
    const last = { x: 0, y: 0 };
    const track = (e) => {
      const p = e.touches && e.touches.length ? e.touches[0] : e.changedTouches && e.changedTouches.length ? e.changedTouches[0] : e;
      if (p && p.clientX != null) { last.x = p.clientX; last.y = p.clientY; }
    };
    ['mousemove', 'mousedown', 'mouseup', 'touchstart', 'touchmove', 'touchend'].forEach((t) => screenRef.current.addEventListener(t, track, { capture: true, passive: true }));
    const sendMouse = (ev) => {
      if (clientRef.current !== client) return;
      const rect = el.getBoundingClientRect();
      const sc = display.getScale() || 1;
      const st = ev.state;
      st.x = Math.max(0, Math.min(display.getWidth() - 1, Math.round((last.x - rect.left) / sc)));
      st.y = Math.max(0, Math.min(display.getHeight() - 1, Math.round((last.y - rect.top) / sc)));
      client.sendMouseState(st, false);
    };
    const mouse = new Guacamole.Mouse(el);
    mouse.onEach(['mousedown', 'mouseup', 'mousemove'], sendMouse);
    const touch = new Guacamole.Mouse.Touchscreen(el);
    touch.onEach(['mousedown', 'mouseup', 'mousemove'], sendMouse);

    // Keyboard is bound to the focusable screen box only, so the rest of the page keeps its keys.
    const kb = new Guacamole.Keyboard(screenRef.current);
    kb.onkeydown = (k) => { if (clientRef.current === client) client.sendKeyEvent(1, k); return false; };
    kb.onkeyup = (k) => { if (clientRef.current === client) client.sendKeyEvent(0, k); };
    kbRef.current = kb;

    client.connect('ticket=' + encodeURIComponent(ticket) + '&token=' + encodeURIComponent(localStorage.getItem('auth_token') || ''));
  };

  const disconnect = () => { teardown(); setState(5); };
  const sendKeys = (keys) => {
    const c = clientRef.current;
    if (!c) return;
    keys.forEach((k) => c.sendKeyEvent(1, k));
    keys.slice().reverse().forEach((k) => c.sendKeyEvent(0, k));
    if (screenRef.current) screenRef.current.focus();
  };
  const sendClip = () => {
    const c = clientRef.current;
    if (!c) return;
    const stream = c.createClipboardStream('text/plain');
    const w = new Guacamole.StringWriter(stream);
    w.sendText(clip);
    w.sendEnd();
    if (screenRef.current) screenRef.current.focus();
  };
  const pasteLocal = async () => { try { setClip(await navigator.clipboard.readText()); } catch (_) {} };
  const fullscreen = () => { const b = wrapRef.current; if (!b) return; if (document.fullscreenElement) document.exitFullscreen(); else b.requestFullscreen && b.requestFullscreen(); };

  const live = state === 1 || state === 2 || state === 3;
  return (
    <div>
      {!live && (
        <form onSubmit={connect} className="mb-3 flex flex-wrap items-end gap-2" autoComplete="off">
          <label className="text-xs text-gray-500">
            <div className="mb-1">Username</div>
            <input value={username} onChange={(ev) => setUsername(ev.target.value)} autoComplete="off" spellCheck={false}
              className="w-44 rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
          </label>
          <label className="text-xs text-gray-500">
            <div className="mb-1">Domain (optional)</div>
            <input value={domain} onChange={(ev) => setDomain(ev.target.value)} autoComplete="off" spellCheck={false} placeholder="local account"
              className="w-36 rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
          </label>
          <label className="text-xs text-gray-500">
            <div className="mb-1">Password</div>
            <input type="password" value={password} onChange={(ev) => setPassword(ev.target.value)} autoComplete="new-password"
              className="w-48 rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
          </label>
          <label className="text-xs text-gray-500">
            <div className="mb-1">Port</div>
            <input value={port} onChange={(ev) => setPort(ev.target.value.replace(/[^\d]/g, '').slice(0, 5))} inputMode="numeric" placeholder="auto"
              title="Leave empty: 3389, or the port read from the server's registry over SSM"
              className={`w-20 rounded-lg border px-2 py-1.5 text-sm ${portOk ? 'border-gray-300' : 'border-red-400 bg-red-50'}`} />
          </label>
          <button type="submit" disabled={!username || !password || busy || !portOk}
            className="rounded-lg bg-sky-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-40">Connect</button>
          <div className="w-full text-[11px] text-gray-400">
            Uses the Windows account on {x.name}. The password is passed to the RDP session once and is not stored.
            Goes direct to {x.privateIp || 'the private IP'} when the RDP port is open from the dashboard, otherwise through SSM port forwarding (no inbound port needed).
            Port empty = 3389, or the port read from the server's registry.
          </div>
        </form>
      )}

      {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

      {live && (
        <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs">
          <span className={`rounded-full px-2 py-0.5 font-medium ${state === 3 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{STATE[state]}</span>
          <span className="text-gray-400">{domain ? domain + '\\' : ''}{username}{port ? ` · port ${port}` : ''}{state === 3 && scale < 1 ? ` · ${Math.round(scale * 100)}%` : ''}</span>
          <div className="ml-auto flex flex-wrap gap-1">
            <button type="button" onClick={() => sendKeys([0xFFE3, 0xFFE9, 0xFFFF])} className="rounded-md px-2 py-1 ring-1 ring-gray-200 hover:bg-gray-50">Ctrl+Alt+Del</button>
            <button type="button" onClick={() => sendKeys([0xFFEB])} className="rounded-md px-2 py-1 ring-1 ring-gray-200 hover:bg-gray-50">Win</button>
            <button type="button" onClick={() => sendKeys([0xFFE9, 0xFF09])} className="rounded-md px-2 py-1 ring-1 ring-gray-200 hover:bg-gray-50">Alt+Tab</button>
            <button type="button" onClick={() => setShowClip((v) => !v)} className={`rounded-md px-2 py-1 ring-1 ring-gray-200 hover:bg-gray-50 ${showClip ? 'bg-sky-50' : ''}`}>Clipboard</button>
            <button type="button" onClick={fullscreen} className="rounded-md px-2 py-1 ring-1 ring-gray-200 hover:bg-gray-50">Fullscreen</button>
            <button type="button" onClick={disconnect} className="rounded-md px-2 py-1 text-red-600 ring-1 ring-red-200 hover:bg-red-50">Disconnect</button>
          </div>
        </div>
      )}
      {live && showClip && (
        <div className="mb-2 rounded-lg border border-gray-200 bg-gray-50 p-2">
          <textarea value={clip} onChange={(ev) => setClip(ev.target.value)} rows={3}
            className="w-full rounded border border-gray-300 p-1.5 font-mono text-xs" placeholder="Text copied on the server appears here. Type or paste text, then Send to server." />
          <div className="mt-1 flex gap-1 text-xs">
            <button type="button" onClick={sendClip} className="rounded-md bg-sky-600 px-2 py-1 text-white">Send to server</button>
            <button type="button" onClick={pasteLocal} className="rounded-md px-2 py-1 ring-1 ring-gray-200">Paste from my clipboard</button>
          </div>
        </div>
      )}

      <div ref={wrapRef} className={`rdp-surface relative w-full overflow-hidden rounded-lg ${live ? 'bg-black' : ''}`}
        style={{ display: live ? 'flex' : 'none', justifyContent: 'center', alignItems: 'center', minHeight: live ? 240 : 0 }}>
        <div ref={screenRef} tabIndex={0} className="outline-none" onMouseDown={() => screenRef.current && screenRef.current.focus()} />
        {state !== 3 && live && <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-300">{STATE[state]} (SSM path can take 10–20 s)</div>}
      </div>
    </div>
  );
}
